import { Injectable } from '@angular/core';

export interface ElectronicSignatureData {
  method: 'draw' | 'upload' | 'initials';
  data: string;
  timestamp: string;
  ipAddress: string;
  userAgent: string;
  documentHash?: string;
  signatureHash?: string;
}

export interface SignatureValidationResult {
  isValid: boolean;
  errors: string[];
  complianceLevel: 'simple' | 'advanced' | 'qualified';
}

@Injectable({
  providedIn: 'root'
})
export class ElectronicSignatureService {

  constructor() { }

  /**
   * Valide une signature électronique selon les normes eIDAS
   * @param signature - Données de signature à valider
   * @returns Résultat de validation avec niveau de conformité
   */
  validateSignature(signature: ElectronicSignatureData): SignatureValidationResult {
    const errors: string[] = [];
    let complianceLevel: 'simple' | 'advanced' | 'qualified' = 'simple';

    // Vérifications de base (signature électronique simple)
    if (!signature.method || !['draw', 'upload', 'initials'].includes(signature.method)) {
      errors.push('Méthode de signature invalide');
    }

    if (!signature.data || signature.data.trim().length === 0) {
      errors.push('Données de signature manquantes');
    }

    if (!signature.timestamp || !this.isValidTimestamp(signature.timestamp)) {
      errors.push('Horodatage invalide ou manquant');
    }

    if (!signature.userAgent || signature.userAgent.length < 10) {
      errors.push('Informations du navigateur insuffisantes');
    }

    // Vérifications spécifiques par méthode
    switch (signature.method) {
      case 'initials':
        if (signature.data.length < 2 || signature.data.length > 10) {
          errors.push('Les initiales doivent contenir entre 2 et 10 caractères');
        }
        if (!/^[A-Za-z.\s]+$/.test(signature.data)) {
          errors.push('Les initiales ne peuvent contenir que des lettres, points et espaces');
        }
        break;
      
      case 'draw':
        if (!signature.data.startsWith('data:image/')) {
          errors.push('Format de signature dessinée invalide');
        }
        break;
      
      case 'upload':
        if (!signature.data.startsWith('data:image/')) {
          errors.push('Format d\'image de signature invalide');
        }
        break;
    }

    // Vérifications pour signature électronique avancée
    if (signature.documentHash && signature.signatureHash) {
      complianceLevel = 'advanced';
      
      if (!this.isValidHash(signature.documentHash)) {
        errors.push('Hash du document invalide');
      }
      
      if (!this.isValidHash(signature.signatureHash)) {
        errors.push('Hash de signature invalide');
      }
    }

    // Vérification de l'intégrité temporelle
    if (!this.isTimestampRecent(signature.timestamp)) {
      errors.push('L\'horodatage est trop ancien (plus de 24h)');
    }

    return {
      isValid: errors.length === 0,
      errors,
      complianceLevel
    };
  }

  /**
   * Génère les métadonnées de signature conformes eIDAS
   */
  generateSignatureMetadata(method: 'draw' | 'upload' | 'initials', data: string): ElectronicSignatureData {
    const timestamp = new Date().toISOString();
    
    return {
      method,
      data,
      timestamp,
      ipAddress: this.getClientIP(),
      userAgent: navigator.userAgent,
      documentHash: this.generateDocumentHash(),
      signatureHash: this.generateSignatureHash(data, timestamp)
    };
  }

  /**
   * Génère un certificat de signature électronique
   */
  generateSignatureCertificate(signature: ElectronicSignatureData, documentId: string): any {
    const validation = this.validateSignature(signature);
    
    return {
      certificateId: this.generateCertificateId(),
      documentId,
      signatureData: signature,
      validation,
      issuedAt: new Date().toISOString(),
      issuer: 'Applisound Electronic Signature Service',
      compliance: {
        regulation: 'eIDAS (EU) No 910/2014',
        level: validation.complianceLevel,
        standard: 'ETSI EN 319 142-1'
      },
      legalValue: {
        admissible: validation.isValid,
        evidenceLevel: this.getEvidenceLevel(validation.complianceLevel),
        juridicalFramework: 'Code civil français - Articles 1366 à 1369'
      }
    };
  }

  /**
   * Vérifie l'intégrité d'un document signé
   */
  verifyDocumentIntegrity(originalHash: string, currentDocument: string): boolean {
    const currentHash = this.hashDocument(currentDocument);
    return originalHash === currentHash;
  }

  /**
   * Génère un rapport de conformité légale
   */
  generateComplianceReport(signature: ElectronicSignatureData): any {
    const validation = this.validateSignature(signature);
    
    return {
      reportId: this.generateReportId(),
      generatedAt: new Date().toISOString(),
      signature,
      validation,
      legalCompliance: {
        eidas: {
          compliant: validation.isValid,
          level: validation.complianceLevel,
          requirements: this.getEidasRequirements(validation.complianceLevel)
        },
        french: {
          codeArticles: ['1366', '1367', '1368', '1369'],
          compliant: validation.isValid,
          legalValue: validation.isValid ? 'Équivalente à une signature manuscrite' : 'Non valide'
        }
      },
      technicalDetails: {
        method: signature.method,
        timestamp: signature.timestamp,
        integrity: !!signature.documentHash,
        nonRepudiation: validation.complianceLevel !== 'simple'
      }
    };
  }

  // Méthodes privées utilitaires

  private isValidTimestamp(timestamp: string): boolean {
    const date = new Date(timestamp);
    return !isNaN(date.getTime()) && date <= new Date();
  }

  private isTimestampRecent(timestamp: string, maxHours: number = 24): boolean {
    const signatureTime = new Date(timestamp);
    const now = new Date();
    const hoursDiff = (now.getTime() - signatureTime.getTime()) / (1000 * 3600);
    return hoursDiff <= maxHours;
  }

  private isValidHash(hash: string): boolean {
    return /^[a-fA-F0-9]{32,128}$/.test(hash);
  }

  private getClientIP(): string {
    // Dans un environnement réel, l'IP devrait être obtenue côté serveur
    return 'IP_TO_BE_DETERMINED_SERVER_SIDE';
  }

  private generateDocumentHash(): string {
    // Génère un hash du document (simulation)
    return this.generateRandomHash();
  }

  private generateSignatureHash(data: string, timestamp: string): string {
    // Génère un hash de la signature (simulation)
    const combined = data + timestamp;
    return btoa(combined).substring(0, 32);
  }

  private generateRandomHash(): string {
    return Array.from({length: 32}, () => Math.floor(Math.random() * 16).toString(16)).join('');
  }

  private generateCertificateId(): string {
    return 'CERT-' + Date.now() + '-' + Math.random().toString(36).substring(2, 8).toUpperCase();
  }

  private generateReportId(): string {
    return 'RPT-' + Date.now() + '-' + Math.random().toString(36).substring(2, 8).toUpperCase();
  }

  private hashDocument(document: string): string {
    // Simulation d'un hash de document
    return btoa(document).substring(0, 32);
  }

  private getEvidenceLevel(complianceLevel: string): string {
    switch (complianceLevel) {
      case 'simple': return 'Faible à modéré';
      case 'advanced': return 'Élevé';
      case 'qualified': return 'Très élevé';
      default: return 'Indéterminé';
    }
  }

  private getEidasRequirements(level: string): string[] {
    const baseRequirements = [
      'Identification du signataire',
      'Intégrité du document',
      'Horodatage de la signature'
    ];

    if (level === 'advanced' || level === 'qualified') {
      baseRequirements.push(
        'Certificat électronique',
        'Dispositif de création sécurisé',
        'Non-répudiation'
      );
    }

    if (level === 'qualified') {
      baseRequirements.push(
        'Certificat qualifié',
        'Dispositif qualifié de création',
        'Prestataire de services de confiance qualifié'
      );
    }

    return baseRequirements;
  }
}